import React, { useState, useEffect } from 'react';
import { AreaChart, Area, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

const UserEngagement = () => {
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState({
    totalUsers: 0,
    activeUsers: 0,
    engagementRate: 0,
    avgSessionDuration: 0
  });
  const [userActivity, setUserActivity] = useState([]);
  const [engagementTrends, setEngagementTrends] = useState([]);

  useEffect(() => {
    fetchEngagementData();
  }, []);

  const fetchEngagementData = async () => {
    try {
      setLoading(true);

      // Fetch user profiles
      const { data: profiles, error: profileError } = await supabase
        ?.from('user_profiles')
        ?.select('*');

      if (profileError) throw profileError;

      // Fetch bookings for engagement calculation
      const { data: bookings, error: bookingError } = await supabase
        ?.from('bookings')
        ?.select('user_id, created_at')
        ?.order('created_at', { ascending: false });

      if (bookingError) throw bookingError;

      // Fetch reviews for engagement
      const { data: reviews, error: reviewError } = await supabase
        ?.from('reviews')
        ?.select('user_id, created_at')
        ?.order('created_at', { ascending: false });

      if (reviewError) throw reviewError;

      // Calculate metrics
      const totalUsers = profiles?.length || 0;
      
      // Active users (users with activity in last 30 days)
      const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
      const activeUserIds = new Set();
      
      bookings?.forEach(b => {
        if (new Date(b?.created_at) >= thirtyDaysAgo) {
          activeUserIds?.add(b?.user_id);
        }
      });
      
      reviews?.forEach(r => {
        if (new Date(r?.created_at) >= thirtyDaysAgo) {
          activeUserIds?.add(r?.user_id);
        }
      });

      const activeUsers = activeUserIds?.size;
      const engagementRate = totalUsers > 0 ? (activeUsers / totalUsers) * 100 : 0;

      setMetrics({
        totalUsers,
        activeUsers,
        engagementRate,
        avgSessionDuration: 12.5 // Mock data - would need session tracking
      });

      // Generate user activity trends (last 7 days)
      const now = new Date();
      const activityData = [];
      
      for (let i = 6; i >= 0; i--) {
        const date = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
        const dateStr = date?.toISOString()?.split('T')?.[0];
        
        const dayBookings = bookings?.filter(b => b?.created_at?.startsWith(dateStr))?.length || 0;
        const dayReviews = reviews?.filter(r => r?.created_at?.startsWith(dateStr))?.length || 0;
        const dayActiveUsers = new Set([
          ...(bookings?.filter(b => b?.created_at?.startsWith(dateStr))?.map(b => b?.user_id) || []),
          ...(reviews?.filter(r => r?.created_at?.startsWith(dateStr))?.map(r => r?.user_id) || [])
        ])?.size;
        
        activityData?.push({
          date: date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          bookings: dayBookings,
          reviews: dayReviews,
          activeUsers: dayActiveUsers
        });
      }
      setUserActivity(activityData);

      // Engagement trends (last 4 weeks)
      const weeklyData = [];
      for (let i = 3; i >= 0; i--) {
        const weekEnd = new Date(now.getTime() - i * 7 * 24 * 60 * 60 * 1000);
        const weekStart = new Date(weekEnd.getTime() - 7 * 24 * 60 * 60 * 1000);
        
        const weekActiveUsers = new Set([
          ...(bookings?.filter(b => {
            const date = new Date(b?.created_at);
            return date >= weekStart && date < weekEnd;
          })?.map(b => b?.user_id) || []),
          ...(reviews?.filter(r => {
            const date = new Date(r?.created_at);
            return date >= weekStart && date < weekEnd;
          })?.map(r => r?.user_id) || [])
        ])?.size;
        
        const weekEngagement = totalUsers > 0 ? (weekActiveUsers / totalUsers) * 100 : 0;
        
        weeklyData?.push({
          week: `Week ${4 - i}`,
          engagement: weekEngagement?.toFixed(1),
          users: weekActiveUsers
        });
      }
      setEngagementTrends(weeklyData);

    } catch (error) {
      console.error('Error fetching engagement data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <Icon name="Users" size={24} />
            <span className="text-blue-100 text-sm">Total Users</span>
          </div>
          <div className="text-3xl font-bold mb-1">
            {metrics?.totalUsers?.toLocaleString()}
          </div>
          <div className="text-sm text-blue-100">Registered users</div>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <Icon name="UserCheck" size={24} />
            <span className="text-green-100 text-sm">Active Users</span>
          </div>
          <div className="text-3xl font-bold mb-1">
            {metrics?.activeUsers?.toLocaleString()}
          </div>
          <div className="text-sm text-green-100">Last 30 days</div>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <Icon name="Activity" size={24} />
            <span className="text-purple-100 text-sm">Engagement Rate</span>
          </div>
          <div className="text-3xl font-bold mb-1">
            {metrics?.engagementRate?.toFixed(1)}%
          </div>
          <div className="text-sm text-purple-100">Active vs total</div>
        </div>

        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <Icon name="Clock" size={24} />
            <span className="text-orange-100 text-sm">Avg Session</span>
          </div>
          <div className="text-3xl font-bold mb-1">
            {metrics?.avgSessionDuration?.toFixed(1)} min
          </div>
          <div className="text-sm text-orange-100">Per user visit</div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User Activity */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Icon name="Activity" size={20} className="mr-2 text-blue-600" />
            Daily User Activity (Last 7 Days)
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={userActivity}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Area type="monotone" dataKey="activeUsers" stackId="1" stroke="#3b82f6" fill="#3b82f6" name="Active Users" />
              <Area type="monotone" dataKey="bookings" stackId="2" stroke="#0d9488" fill="#0d9488" name="Bookings" />
              <Area type="monotone" dataKey="reviews" stackId="2" stroke="#8b5cf6" fill="#8b5cf6" name="Reviews" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Engagement Trends */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Icon name="TrendingUp" size={20} className="mr-2 text-purple-600" />
            Engagement Rate Trends (4 Weeks)
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={engagementTrends}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="week" />
              <YAxis />
              <Tooltip formatter={(value) => `${value}%`} />
              <Legend />
              <Line type="monotone" dataKey="engagement" stroke="#8b5cf6" strokeWidth={2} name="Engagement Rate (%)" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Engagement Insights */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Icon name="Lightbulb" size={20} className="mr-2 text-yellow-600" />
          Engagement Insights
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center mb-2">
              <Icon name="TrendingUp" size={18} className="text-blue-600 mr-2" />
              <span className="font-semibold text-blue-900">User Growth</span>
            </div>
            <p className="text-sm text-blue-700">
              {metrics?.totalUsers > 0 ? 'Steady' : 'New'} user base with {metrics?.activeUsers} active members engaging regularly
            </p>
          </div>

          <div className="p-4 bg-green-50 rounded-lg border border-green-200">
            <div className="flex items-center mb-2">
              <Icon name="CheckCircle" size={18} className="text-green-600 mr-2" />
              <span className="font-semibold text-green-900">Engagement Quality</span>
            </div>
            <p className="text-sm text-green-700">
              {metrics?.engagementRate >= 50 ? 'High' : metrics?.engagementRate >= 25 ? 'Moderate' : 'Growing'} engagement rate indicates {metrics?.engagementRate >= 50 ? 'strong' : 'developing'} user interest
            </p>
          </div>

          <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
            <div className="flex items-center mb-2">
              <Icon name="Target" size={18} className="text-purple-600 mr-2" />
              <span className="font-semibold text-purple-900">Retention Focus</span>
            </div>
            <p className="text-sm text-purple-700">
              Focus on converting {metrics?.totalUsers - metrics?.activeUsers} inactive users to boost overall engagement
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserEngagement;